import { PageLoader } from '@/components/ui/loader';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarDays, Mail, RefreshCw, Unplug } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/misc';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

type Mailbox = {
  id: string;
  provider: 'GOOGLE' | 'MICROSOFT';
  email: string;
  syncMail: boolean;
  syncCalendar: boolean;
  isActive: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
};

const PROVIDERS = [
  { key: 'google', provider: 'GOOGLE', name: 'Gmail / Google Workspace', colour: '#EA4335' },
  { key: 'microsoft', provider: 'MICROSOFT', name: 'Outlook / Microsoft 365', colour: '#0078D4' },
] as const;

const RESULTS: Record<string, [string, 'success' | 'error']> = {
  connected: ['Mailbox connected — mail with your customers will appear on their records', 'success'],
  cancelled: ['Connection cancelled', 'error'],
  expired: ['That took too long — try connecting again', 'error'],
  error: ['The mailbox could not be connected — try again', 'error'],
};

/**
 * Each rep's own Gmail or Outlook (HubSpot's connected inbox): customer mail
 * logged on records, email sent as them, and their calendar keeping booking
 * pages honest.
 */
export function MailboxPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ['sales', 'my-mailbox'],
    queryFn: () => apiGet<{ providers: { google: boolean; microsoft: boolean }; mailboxes: Mailbox[] }>('/sales/mailbox'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sales', 'my-mailbox'] });
  const fail = (error: unknown) => toast.error(error instanceof ApiRequestError ? error.message : 'Something went wrong');

  const connect = useMutation({
    mutationFn: (provider: string) => apiPost<{ url: string }>(`/sales/mailbox/${provider}/connect`),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: fail,
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; syncMail?: boolean; syncCalendar?: boolean }) => apiPatch(`/sales/mailbox/${id}`, body),
    onSuccess: refresh,
    onError: fail,
  });
  const sync = useMutation({
    mutationFn: (id: string) => apiPost(`/sales/mailbox/${id}/sync`),
    onSuccess: () => {
      refresh();
      toast.success('Synced');
    },
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: string) => apiDelete(`/sales/mailbox/${id}`), onSuccess: refresh, onError: fail });

  useEffect(() => {
    const result = params.get('result');
    if (!result) return;
    const [message, kind] = RESULTS[result] ?? RESULTS.error;
    (kind === 'success' ? toast.success : toast.error)(message);
    setParams({}, { replace: true });
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PageHeader
        title="My email & calendar"
        description="Connect your own mailbox. Emails with your customers are logged on their records, replies stop sequences, you can send as yourself, and booking pages skip times your calendar is busy."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'My email & calendar' }]}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {PROVIDERS.map((item) => {
            const box = data?.mailboxes.find((mailbox) => mailbox.provider === item.provider);
            const available = data?.providers[item.key];
            return (
              <Card key={item.key}>
                <CardContent className="space-y-4 pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ backgroundColor: item.colour }}>
                        <Mail className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{box ? box.email : 'Not connected'}</p>
                      </div>
                    </div>
                    {box && (
                      <Badge className={box.isActive && !box.lastError ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {box.isActive ? (box.lastError ? 'Needs attention' : 'Connected') : 'Reconnect'}
                      </Badge>
                    )}
                  </div>

                  {box ? (
                    <>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <Switch checked={box.syncMail} onCheckedChange={(value) => update.mutate({ id: box.id, syncMail: value })} />
                          <Mail className="h-4 w-4 text-muted-foreground" /> Log email with customers on their records
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <Switch checked={box.syncCalendar} onCheckedChange={(value) => update.mutate({ id: box.id, syncCalendar: value })} />
                          <CalendarDays className="h-4 w-4 text-muted-foreground" /> Use my calendar for booking pages
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {box.lastSyncAt ? `Last synced ${formatDateTime(box.lastSyncAt)}` : 'Not synced yet'}
                        {box.lastError ? ` · ${box.lastError}` : ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {!box.isActive ? (
                          <Button size="sm" onClick={() => connect.mutate(item.key)}>
                            Reconnect
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" disabled={sync.isPending} onClick={() => sync.mutate(box.id)}>
                            <RefreshCw /> Sync now
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.confirm(`Disconnect ${box.email}? Mail already logged stays on the records.`) && remove.mutate(box.id)}
                        >
                          <Unplug /> Disconnect
                        </Button>
                      </div>
                    </>
                  ) : available ? (
                    <Button onClick={() => connect.mutate(item.key)} disabled={connect.isPending}>
                      Connect {item.provider === 'GOOGLE' ? 'Google' : 'Microsoft'}
                    </Button>
                  ) : (
                    <p className="rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                      Coming soon — your administrator is finishing the {item.provider === 'GOOGLE' ? 'Google' : 'Microsoft'} sign-in setup.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <p className="mt-6 max-w-3xl text-xs text-muted-foreground">
        Only mail with people already in the CRM is logged — your private mail is never read into the workspace. You can
        disconnect at any time.
      </p>
    </div>
  );
}
